import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  ShoppingCart as CartIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { hasPermission } from '../services/authService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function AnalyticsDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [visitorDays, setVisitorDays] = useState('30');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    format: 'json',
    startDate: '',
    endDate: ''
  });

  const isAdmin = hasPermission('view_analytics');

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/analytics?action=dashboard&dateRange=${dateRange}`);
      
      if (!response.ok) {
        throw new Error('Failed to load analytics data');
      }
      
      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const loadVisitorData = useCallback(async () => {
    try {
      const response = await fetch(`/api/analytics?action=visitor-count&days=${visitorDays}`);
      
      if (!response.ok) {
        throw new Error('Failed to load visitor data');
      }
      
      const data = await response.json();
      setVisitorData(data);
    } catch (err) {
      console.error('Error loading visitor data:', err);
      // Don't set error for visitor data as it's not critical
    }
  }, [visitorDays]);

  useEffect(() => {
    if (isAdmin) {
      loadDashboardData();
      loadVisitorData();
    }
  }, [isAdmin, loadDashboardData, loadVisitorData]);

  useEffect(() => {
    if (isAdmin) {
      loadVisitorData();
    }
  }, [isAdmin, visitorDays, loadVisitorData]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        action: 'export',
        format: exportConfig.format,
        ...(exportConfig.startDate && { startDate: exportConfig.startDate }),
        ...(exportConfig.endDate && { endDate: exportConfig.endDate })
      });

      const response = await fetch(`/api/analytics?${params}`);
      
      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-export.${exportConfig.format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setExportDialogOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to view analytics. Please contact an administrator.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadDashboardData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No analytics data available.</Alert>
      </Box>
    );
  }

  const { overview, recentActivity, popularCableTypes, popularConnectors, dailyTrends } = dashboardData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Analytics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small">
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDashboardData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Sessions"
            value={overview.totalSessions}
            icon={<PeopleIcon />}
            color="primary"
            trend={overview.totalSessions > 1000 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Completions"
            value={overview.totalCompletions}
            icon={<CheckCircleIcon />}
            color="success"
            trend={overview.conversionRate > 0.1 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Cart Additions"
            value={overview.totalCartAdditions}
            icon={<CartIcon />}
            color="warning"
            trend={overview.totalCartAdditions > 500 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Conversion Rate"
            value={`${(overview.conversionRate * 100).toFixed(1)}%`}
            icon={<TrendingUpIcon />}
            color="info"
            trend={overview.conversionRate > 0.1 ? 'up' : 'down'}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Daily Trends */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailyTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="events" stackId="1" stroke="#8884d8" fill="#8884d8" />
                  <Area type="monotone" dataKey="sessions" stackId="2" stroke="#82ca9d" fill="#82ca9d" />
                  <Area type="monotone" dataKey="completions" stackId="3" stroke="#ffc658" fill="#ffc658" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Popular Cable Types */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Popular Cable Types
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={popularCableTypes}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {popularCableTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Popular Connectors Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Popular Connectors
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={popularConnectors}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="connector" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Visitor Count Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Visitor Count by Day
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={visitorDays}
                    onChange={(e) => setVisitorDays(e.target.value)}
                    label="Time Range"
                  >
                    <MenuItem value="7">Last 7 Days</MenuItem>
                    <MenuItem value="14">Last 14 Days</MenuItem>
                    <MenuItem value="30">Last 30 Days</MenuItem>
                    <MenuItem value="60">Last 60 Days</MenuItem>
                    <MenuItem value="90">Last 90 Days</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {visitorData ? (
                <>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip 
                      label={`Total Visitors: ${visitorData.summary.totalVisitors}`} 
                      color="primary" 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`Total Sessions: ${visitorData.summary.totalSessions}`} 
                      color="secondary" 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`Avg/Day: ${visitorData.summary.averageVisitorsPerDay}`} 
                      color="success" 
                      variant="outlined" 
                    />
                  </Box>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={visitorData.visitorData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(value) => new Date(value).toLocaleDateString()}
                      />
                      <YAxis />
                      <RechartsTooltip 
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                        formatter={(value, name) => [value, name === 'uniqueVisitors' ? 'Unique Visitors' : 'Total Sessions']}
                      />
                      <Bar dataKey="uniqueVisitors" fill="#8884d8" name="Unique Visitors" />
                      <Bar dataKey="totalSessions" fill="#82ca9d" name="Total Sessions" />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity Table */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivity.slice(0, 10).map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          {new Date(activity.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={activity.userId ? activity.userId.substring(0, 8) : 'Unknown'} 
                            size="small" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={activity.type} 
                            size="small"
                            color={getEventColor(activity.type)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {formatEventDetails(activity.data)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Analytics Data</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select
                  value={exportConfig.format}
                  onChange={(e) => setExportConfig({ ...exportConfig, format: e.target.value })}
                  label="Format"
                >
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={exportConfig.startDate}
                onChange={(e) => setExportConfig({ ...exportConfig, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={exportConfig.endDate}
                onChange={(e) => setExportConfig({ ...exportConfig, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExport} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Metric Card Component
function MetricCard({ title, value, icon, color, trend }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: `${color}.main`,
            gap: 1 
          }}>
            {icon}
            {trend === 'up' ? (
              <TrendingUpIcon color="success" />
            ) : (
              <TrendingDownIcon color="error" />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Helper functions
function getEventColor(eventType) {
  const colorMap = {
    'session_start': 'primary',
    'cable_completion': 'success',
    'cart_addition': 'warning',
    'error': 'error',
    'page_view': 'info'
  };
  return colorMap[eventType] || 'default';
}

function formatEventDetails(data) {
  if (!data) return '';
  
  if (data.configType) {
    return `${data.configType} cable - ${data.connectors?.endA || 'N/A'} to ${data.connectors?.endB || 'N/A'}`;
  }
  
  if (data.page) {
    return `Page: ${data.page}`;
  }
  
  if (data.errorType) {
    return `Error: ${data.errorType}`;
  }
  
  return JSON.stringify(data).substring(0, 50) + '...';
} 